// repo/primary-care-connect/frontend/src/api/portalFamilyDoctor.ts

export type IdName = { id: number; name: string };

export type FamilyDoctorInsightsSourceScope = "all" | "public" | "navigator";
export type FamilyDoctorInsightsTimeRange = "30" | "90" | "all" | "custom";
export type FamilyDoctorInsightsBaseline =
  | "all"
  | "attached"
  | "unattached";

export type FamilyDoctorIntakeRule = {
  id: number;
  familyDoctorId: number;
  criterionKey: string | null;
  criterionValue: string | null;
  matchBehavior: string | null;
  weight: number | null;
  isHardRule: boolean;
  isActive: boolean;
  createdAt: string | null;
  updatedAt: string | null;
};

export type FamilyDoctorProfileResponse = {
  familyDoctorId: number;

  doctor: {
    // Identity (read-only)
    fullName: string | null;
    gender: string | null;

    // Keep existing key used in older UI
    cpsoNumber: string | null;

    // Also support new key returned by backend (we can map either)
    cpso?: string | null;

    // Intake (editable section)
    acceptingNewPatients: boolean;
    acceptingStatusUpdatedAt?: string | null; // ISO datetime
    intakeNotes?: string | null;
    firstAppointmentBookingUrl?: string | null;

    // Capacity (editable section)
    declaredCapacity?: number | null;
    remainingCapacityOverride?: number | null;
    remainingCapacityUpdatedAt?: string | null; // ISO datetime
  };

  services?: {
    languages: IdName[];
    visitTypes: IdName[];
    coverages: IdName[];
  };

  intakeRules?: FamilyDoctorIntakeRule[];

  clinic: {
    clinicId: number;
    name: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    city: string | null;
    province: string;
    postalCode: string | null;
    phone: string | null;
    fax: string | null;
    website: string | null;
    adminEmail: string | null;

    // Optional richer clinic context (read-only section)
    clinicSetting?: { id: number; name: string | null } | null;

    // NEW: surfaced from Clinic.ClinicSettingsJson (parsed + raw)
    clinicSettingsJsonRaw?: string | null;
    clinicSettings?: any | null;

    operatingHours?: Array<{
      dayOfWeek: number;
      openTime: string | null; // HH:MM:SS
      closeTime: string | null; // HH:MM:SS
      isClosed: boolean;
    }>;
    accessibilityFeatures?: IdName[];
    onSiteServices?: IdName[];
  };

  user: {
    userId: number;
    email: string | null;
    displayName: string | null;
  };

  visibilityHints?: any;
};

export type AcceptingStatusResponse = {
  familyDoctorId: number;
  acceptingNewPatients: boolean;
  acceptingStatusUpdatedAt: string; // ISO UTC
  updatedAt: string; // ISO UTC

  // Backward-compatible alias from older dashboard code.
  // New UI should use acceptingStatusUpdatedAt.
  heartbeatAt: string; // ISO UTC
};

export type UpdateIntakeRequest = {
  acceptingNewPatients: boolean;
  intakeNotes: string | null;
  firstAppointmentBookingUrl: string | null;
};

export type UpdateIntakeResponse = {
  doctor: {
    acceptingNewPatients: boolean;
    acceptingStatusUpdatedAt: string | null; // ISO datetime
    intakeNotes: string | null;
    firstAppointmentBookingUrl: string | null;
  };
};

export type UpdateIntakeRulesRequest = {
  rules: Array<{
    criterionKey: string;
    criterionValue: string;
    matchBehavior: string;
    weight: number;
    isHardRule: boolean;
    isActive: boolean;
  }>;
};

export type UpdateIntakeRulesResponse = {
  intakeRules: FamilyDoctorIntakeRule[];
};

export type UpdateCapacityRequest = {
  declaredCapacity: number | null;
  remainingCapacityOverride: number | null;
};

export type UpdateCapacityResponse = {
  ok?: boolean;
  doctor: {
    familyDoctorProfileId: number;
    declaredCapacity: number | null;
    remainingCapacityOverride: number | null;
    remainingCapacityUpdatedAt: string | null; // ISO datetime
  };
};

export type FamilyDoctorInsightsResponse = {
  ok?: boolean;

  familyDoctorId: number;

  filters?: {
    sourceScope?: FamilyDoctorInsightsSourceScope;
    timeRange?: FamilyDoctorInsightsTimeRange;
    baseline?: FamilyDoctorInsightsBaseline;
    from?: string | null;
    to?: string | null;
  };

  doctor: {
    familyDoctorProfileId: number;
    clinicId: number;
    fullName: string | null;
  };

  capacity: {
    declaredCapacity: number | null;
    usedCapacity: number;
    remainingCapacityOverride: number | null;
    remainingCapacity: number | null;
    remainingCapacityUpdatedAt: string | null;
    capacitySource: "override" | "computed" | "missing" | string;
  };

  throughput: {
    attachments30d: number;
    attachmentsInRange?: number;
    rangeDays?: number;
    pacePerDay: number;
    avgDaysToAttachment: number | null;
    firstAttachmentCompletedAt?: string | null;
    lastAttachmentCompletedAt: string | null;
  };

  forecast?: {
    estimatedDaysToFullCapacity: number | null;
    forecastStatus:
      | "ready"
      | "capacityNotSet"
      | "capacityReached"
      | "notEnoughPace"
      | string;
    forecastMessage: string | null;
  };

  pipeline: {
    submitted: number;
    inReview: number;
    waitlist: number;
    acceptedPendingAttachment: number;
    totalActive: number;
  };
};

export type FamilyDoctorInsightsArgs = {
  sourceScope?: FamilyDoctorInsightsSourceScope;
  timeRange?: FamilyDoctorInsightsTimeRange;
  baseline?: FamilyDoctorInsightsBaseline;
  from?: string | null;
  to?: string | null;
};

export type UpdateServicesRequest = {
  languageIds: number[];
  visitTypeIds: number[];
  coverageTypeIds: number[];
};

export type UpdateServicesResponse = {
  services: {
    languages: IdName[];
    visitTypes: IdName[];
    coverages: IdName[];
  };
};

export class ApiError extends Error {
  status: number;
  body?: any;

  constructor(message: string, status: number, body?: any) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function requestJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      ...(init?.headers || {}),
    },
    ...init,
  });

  const text = await res.text().catch(() => "");
  let body: any = null;

  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text || null;
  }

  if (!res.ok) {
    throw new ApiError(
      body?.error || `Request failed (${res.status})`,
      res.status,
      body
    );
  }

  return body as T;
}

function buildQuery(params: Record<string, string | number | null | undefined>) {
  const sp = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;

    const s = String(value).trim();
    if (!s) continue;

    sp.set(key, s);
  }

  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

/**
 * GET /api/portal/family-doctor/profile
 * Returns the logged-in family doctor's composite profile + clinic context
 */
export async function getFamilyDoctorProfile(): Promise<FamilyDoctorProfileResponse> {
  return requestJson<FamilyDoctorProfileResponse>(
    "/api/portal/family-doctor/profile",
    {
      method: "GET",
    }
  );
}

/**
 * GET /api/portal/family-doctor/insights
 * Returns doctor-scoped capacity, pipeline, and attachment insights.
 */
export async function getFamilyDoctorInsights(
  args: FamilyDoctorInsightsArgs = {}
): Promise<FamilyDoctorInsightsResponse> {
  const qs = buildQuery({
    sourceScope: args.sourceScope ?? null,
    timeRange: args.timeRange ?? null,
    baseline: args.baseline ?? null,
    from: args.from ?? null,
    to: args.to ?? null,
  });

  return requestJson<FamilyDoctorInsightsResponse>(
    `/api/portal/family-doctor/insights${qs}`,
    {
      method: "GET",
    }
  );
}

/**
 * PATCH /api/portal/family-doctor/profile/intake
 * Updates intake section fields (one Save button).
 */
export async function updateFamilyDoctorIntake(
  payload: UpdateIntakeRequest
): Promise<UpdateIntakeResponse> {
  return requestJson<UpdateIntakeResponse>(
    "/api/portal/family-doctor/profile/intake",
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
}

/**
 * PUT /api/portal/family-doctor/profile/intake-rules
 * Replace-all doctor intake rules used by Community Navigator matching.
 */
export async function updateFamilyDoctorIntakeRules(
  payload: UpdateIntakeRulesRequest
): Promise<UpdateIntakeRulesResponse> {
  return requestJson<UpdateIntakeRulesResponse>(
    "/api/portal/family-doctor/profile/intake-rules",
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
}

/**
 * PATCH /api/portal/family-doctor/profile/capacity
 * Updates capacity section fields (one Save button).
 */
export async function updateFamilyDoctorCapacity(
  payload: UpdateCapacityRequest
): Promise<UpdateCapacityResponse> {
  return requestJson<UpdateCapacityResponse>(
    "/api/portal/family-doctor/profile/capacity",
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
}

/**
 * PUT /api/portal/family-doctor/profile/services
 * Replace-all service mappings (languages, visit types, coverage) (one Save button).
 */
export async function updateFamilyDoctorServices(
  payload: UpdateServicesRequest
): Promise<UpdateServicesResponse> {
  return requestJson<UpdateServicesResponse>(
    "/api/portal/family-doctor/profile/services",
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
}

/**
 * PUT /api/portal/family-doctor/accepting-status
 * Body: { acceptingNewPatients: boolean }
 *
 * Updates accepting status and returns the persisted accepting-status heartbeat.
 */
export async function updateAcceptingNewPatientsStatus(
  acceptingNewPatients: boolean
): Promise<AcceptingStatusResponse> {
  return requestJson<AcceptingStatusResponse>(
    "/api/portal/family-doctor/accepting-status",
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ acceptingNewPatients }),
    }
  );
}